import { RcFile, UploadFile } from 'antd/lib/upload/interface'
import { OutboxMessage } from './slices/forumOutboxSlice'

const DATABASE = 'forum-outbox-v1'
let database: Promise<IDBDatabase> | undefined
const logoutGeneration = new Map<number | undefined, number>()

const open = () => {
  if (!database) {
    database = new Promise<IDBDatabase>((resolve, reject) => {
      if (typeof indexedDB === 'undefined') return reject(new Error('Хранилище файлов недоступно в этом браузере.'))
      const request = indexedDB.open(DATABASE, 1)
      request.onupgradeneeded = () => {
        const jobs = request.result.createObjectStore('jobs', { keyPath: 'key' })
        jobs.createIndex('userId', 'userId')
        const files = request.result.createObjectStore('files', { keyPath: 'key' })
        files.createIndex('userId', 'userId')
        files.createIndex('jobKey', 'jobKey')
      }
      request.onsuccess = () => resolve(request.result)
      request.onerror = () => reject(request.error)
      request.onblocked = () => reject(new Error('Хранилище файлов заблокировано другой вкладкой.'))
    }).catch((error) => {
      database = undefined
      throw error
    })
  }
  return database
}

const done = (transaction: IDBTransaction) =>
  new Promise<void>((resolve, reject) => {
    transaction.oncomplete = () => resolve()
    transaction.onerror = () => reject(transaction.error)
    transaction.onabort = () => reject(transaction.error || new Error('Сохранение файлов прервано.'))
  })

const result = <T>(request: IDBRequest<T>) =>
  new Promise<T>((resolve, reject) => {
    request.onsuccess = () => resolve(request.result)
    request.onerror = () => reject(request.error)
  })

const metadata = (job: OutboxMessage) => ({
  ...job,
  files: job.files.map((file) => {
    const { originFileObj, ...info } = file
    return {
      ...info,
      localFile: !!originFileObj,
      thumbUrl: undefined,
      url: info.url?.startsWith('blob:') ? undefined : info.url,
    }
  }),
})

const deleteIndex = (index: IDBIndex, value: string | number) => {
  const request = index.openCursor(IDBKeyRange.only(value))
  request.onsuccess = () => {
    const cursor = request.result
    if (cursor) {
      cursor.delete()
      cursor.continue()
    }
  }
}

export const outboxStorage = {
  async save(job: OutboxMessage) {
    const generation = logoutGeneration.get(job.userId)
    const db = await open()
    if (generation !== logoutGeneration.get(job.userId)) throw new Error('Отправка отменена при выходе из аккаунта.')
    const transaction = db.transaction(['jobs', 'files'], 'readwrite')
    const completion = done(transaction)
    transaction.objectStore('jobs').put(metadata(job))
    for (const file of job.files) {
      if (file.originFileObj) {
        transaction.objectStore('files').put({
          key: `${job.key}:${file.uid}`,
          jobKey: job.key,
          userId: job.userId,
          blob: file.originFileObj,
        })
      }
    }
    await completion
  },
  async checkpoint(job: OutboxMessage) {
    const db = await open()
    const transaction = db.transaction('jobs', 'readwrite')
    const completion = done(transaction)
    const store = transaction.objectStore('jobs')
    const request = store.getKey(job.key)
    request.onsuccess = () => {
      if (request.result !== undefined) store.put(metadata(job))
    }
    await completion
  },
  async load(userId: number): Promise<OutboxMessage[]> {
    const db = await open()
    const transaction = db.transaction(['jobs', 'files'], 'readonly')
    const [jobs, files] = await Promise.all([
      result(transaction.objectStore('jobs').index('userId').getAll(userId)),
      result(transaction.objectStore('files').index('userId').getAll(userId)),
    ])
    const blobs = new Map<string, File>(files.map((file) => [file.key, file.blob]))
    return jobs.map((job) => ({
      ...job,
      files: job.files.map((file: UploadFile & { localFile?: boolean }) => {
        const { localFile, ...info } = file
        const blob = blobs.get(`${job.key}:${file.uid}`)
        if (localFile && !blob) throw new Error('Не удалось восстановить сохранённый файл.')
        return {
          ...info,
          originFileObj: blob ? (Object.assign(blob, { uid: file.uid }) as RcFile) : undefined,
        }
      }),
    }))
  },
  async remove(key: string) {
    const db = await open()
    const transaction = db.transaction(['jobs', 'files'], 'readwrite')
    const completion = done(transaction)
    transaction.objectStore('jobs').delete(key)
    deleteIndex(transaction.objectStore('files').index('jobKey'), key)
    await completion
  },
  async clearUser(userId: number) {
    logoutGeneration.set(userId, (logoutGeneration.get(userId) || 0) + 1)
    const db = await open()
    const transaction = db.transaction(['jobs', 'files'], 'readwrite')
    const completion = done(transaction)
    deleteIndex(transaction.objectStore('jobs').index('userId'), userId)
    deleteIndex(transaction.objectStore('files').index('userId'), userId)
    await completion
  },
}
